import React, { useEffect, useState } from 'react'
import { Table, Button, Modal, Input, Space, Select, DatePicker } from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SlackOutlined,
} from '@ant-design/icons'
import axiosInstance from '../../Utils/axiosInstance'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'

import {
  CreateColoringDataAPI,
  DeleteColoringDataAPI,
  GetColoringDataAPI,
  PendingRawItemProductCount,
  TotalRawItemProduct,
  UpdateColoringDataAPI,
} from '../../AllAPI/AdminAPI'

const Coloring = () => {
  const selectedYear = Number(sessionStorage.getItem('year'))
  const [raws, setRaws] = useState([])
  const [rawProducts, setRawProducts] = useState([])
  const [searchText, setSearchText] = useState('')
  const [tableLoading, setTableLoading] = useState(false)
  const [rawProductLoading, setRawProductLoading] = useState(false)
  const [pendingQtyLoading, setPendingQtyLoading] = useState(false)
  const [saveLoading, setSaveLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  /* ================= Modal + Inputs ================= */
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingId, setEditingId] = useState(null)

  const [date, setDate] = useState(null)
  const [rawProductId, setRawProductId] = useState(null)
  const [productDetails, setProductDetails] = useState(null) // remaining qty

  const [quantity, setQuantity] = useState('')
  const [originalQty, setOriginalQty] = useState(0) //  NEW

  /* ================= Fetch product pending qty ================= */
  const handleRawProductChange = async (value) => {
    setRawProductId(value)
    fetchRawProductDetails(value)
  }

  const fetchRawProductDetails = async (productId) => {
    try {
      setPendingQtyLoading(true)

      const res = await axiosInstance.get(
        `${PendingRawItemProductCount}/${productId}`,
      )

      setProductDetails(res.data)
    } catch {
      toast.error('Failed to load product details')
      setProductDetails(null)
    } finally {
      setPendingQtyLoading(false)
    }
  }

  /* ================= Delete ================= */
  const [deleteId, setDeleteId] = useState(null)

  useEffect(() => {
    fetchRawProducts()
    fetchColoringData()
  }, [])

  /* ================= Fetch Coloring Data ================= */
  const fetchColoringData = async () => {
    try {
      setTableLoading(true)

      const res = await axiosInstance.get(
        `${GetColoringDataAPI}/${selectedYear}`,
      )

      setRaws(Array.isArray(res.data) ? res.data : [])
    } catch {
      toast.error('Failed to load data')
    } finally {
      setTableLoading(false)
    }
  }

  /* ================= Fetch Raw Products ================= */
  const fetchRawProducts = async () => {
    try {
      setRawProductLoading(true)

      const res = await axiosInstance.get(
        `${TotalRawItemProduct}/${selectedYear}`,
      )

      setRawProducts(Array.isArray(res.data) ? res.data : [])
    } catch {
      toast.error('Failed to load products')
    } finally {
      setRawProductLoading(false)
    }
  }

  /* ================= Open Modal ================= */
  const openModal = (record = null) => {
    if (record) {
      setEditingId(record.id)
      setDate(dayjs(record.date))
      setRawProductId(record.prod_id)
      setQuantity(record.quantity)
      setOriginalQty(record.quantity) //  STORE OLD QTY
      fetchRawProductDetails(record.prod_id)
    } else {
      setEditingId(null)
      setDate(null)
      setRawProductId(null)
      setQuantity('')
      setOriginalQty(0)
      setProductDetails(null)
    }
    setIsModalOpen(true)
  }

  /* ================= Save / Update ================= */
  const handleSave = async () => {
    if (!date) return toast.error('Date is required')
    if (!rawProductId) return toast.error('Product is required')
    if (!quantity || Number(quantity) <= 0)
      return toast.error('Enter valid quantity')

    const objData = {
      date: date.format('YYYY-MM-DD'),
      prod_id: rawProductId,
      quantity: Number(quantity),
      year: selectedYear,
    }

    try {
      setSaveLoading(true)

      let res

      if (editingId) {
        res = await axiosInstance.put(
          `${UpdateColoringDataAPI}/${editingId}`,
          objData,
        )
      } else {
        res = await axiosInstance.post(CreateColoringDataAPI, objData)
      }

      if (res.data === 'UPDATED') {
        toast.success('Coloring Updated')
      } else if (res.data === 'SAVED') {
        toast.success('Coloring Added')
      } else {
        toast.warn('Error Occurred! Try Again')
      }

      fetchColoringData()
      closeModal()
    } catch {
      toast.error('Failed to save data')
    } finally {
      setSaveLoading(false)
    }
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingId(null)
    setDate(null)
    setRawProductId(null)
    setQuantity('')
    setOriginalQty(0)
    setProductDetails(null)
  }

  /* ================= Delete ================= */
  const confirmDelete = async () => {
    try {
      setDeleteLoading(true)

      const res = await axiosInstance.delete(
        `${DeleteColoringDataAPI}/${deleteId}`,
      )

      if (res.data === 'DELETED') {
        toast.success('Coloring data deleted successfully')
        setDeleteId(null)
        fetchColoringData()
      }
    } catch {
      toast.error('Delete failed')
    } finally {
      setDeleteLoading(false)
    }
  }

  /* ================= Search ================= */
  const filteredRaws = raws.filter((r) => {
    const productName = r?.production?.product_name?.toLowerCase() || ''
    const dateText = r?.date || ''
    const search = searchText.toLowerCase()

    return productName.includes(search) || dateText.includes(search)
  })

  /* ================= Quantity Max Logic ================= */
  const maxQty = editingId
    ? (productDetails || 0) + originalQty
    : productDetails || 0

  /* ================= Table ================= */
  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      render: (d) => dayjs(d).format('DD-MM-YYYY'),
    },

    {
      title: 'Product Name',
      dataIndex: 'production',
      render: (p) => (p ? `${p.product_name} (${p.size})` : '-'),
    },
    { title: 'Quantity', dataIndex: 'quantity' },
    {
      title: 'Actions',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => openModal(record)}
          />
          <Button
            icon={<DeleteOutlined />}
            size="small"
            danger
            onClick={() => setDeleteId(record.id)}
          />
        </Space>
      ),
    },
  ]

  return (
    <>
      <div className="mainContainer">
        <div className="header-raw">
          <h3>
            <SlackOutlined style={{ color: 'orangered' }} /> Coloring
          </h3>
        </div>

        <div className="search-bar text-end">
          <Button
            className="addButton mx-md-2"
            icon={<PlusOutlined />}
            onClick={() => openModal()}
          >
            Add
          </Button>

          <Input
            placeholder="Search by product..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 250 }}
          />
        </div>

        <Table
          columns={columns}
          dataSource={filteredRaws}
          rowKey="id"
          loading={tableLoading}
          bordered
          className="shadow"
          style={{ overflowX: 'auto' }}
        />
      </div>

      {/* ================= MODAL ================= */}
      <Modal
        title={editingId ? 'Edit Colored Idol' : 'Add Colored Idol'}
        open={isModalOpen}
        onCancel={closeModal}
        footer={[
          <Button key="cancel" onClick={closeModal}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleSave}
            loading={saveLoading}
          >
            {editingId ? 'Update' : 'Save'}
          </Button>,
        ]}
      >
        <label>Date</label>
        <DatePicker value={date} onChange={setDate} style={{ width: '100%' }} />

        <label className="mt-2">Raw Idol Product</label>
        <Select
          value={rawProductId}
          onChange={handleRawProductChange}
          placeholder="Select product"
          style={{ width: '100%' }}
          disabled={!!editingId}
          loading={rawProductLoading}
        >
          {rawProducts.map((p) => (
            <Select.Option key={p.prod_id} value={p.prod_id}>
              {p.product_name} ({p.size})
            </Select.Option>
          ))}
        </Select>

        {pendingQtyLoading && (
          <p style={{ color: 'gray', marginTop: 8 }}>
            Loading pending quantity...
          </p>
        )}

        {!pendingQtyLoading && productDetails !== null && (
          <p style={{ color: 'gray', marginTop: 8 }}>
            Pending Quantity: {productDetails}
          </p>
        )}

        <label className="mt-2">Quantity</label>
        <Input
          type="number"
          min={1}
          max={maxQty}
          value={quantity}
          step="1"
          onKeyDown={(e) => {
            if (e.key === '.' || e.key === ',') {
              e.preventDefault()
            }
          }}
          placeholder="Enter quantity"
          onChange={(e) => {
            const value = Number(e.target.value)
            if (value > maxQty) {
              toast.warn(`Maximum available quantity is ${maxQty}`)
              setQuantity(maxQty)
            } else {
              setQuantity(value)
            }
          }}
        />
      </Modal>

      {/* ================= DELETE ================= */}
      <Modal
        title="Confirm Delete"
        open={!!deleteId}
        onCancel={() => setDeleteId(null)}
        onOk={confirmDelete}
        okText="Delete"
        confirmLoading={deleteLoading}
        okButtonProps={{ danger: true }}
      >
        Are you sure you want to delete this Coloring data entry?
      </Modal>
    </>
  )
}

export default Coloring
